import { getNoticeByIdAPI, putNoticeByIdAPI } from '@/src/apis/rest/notice';
import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import Input from '@/src/components/atom/Input';
import RadioGroup from '@/src/components/atom/RadioGroup';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import FormContent from '@/src/components/molecule/FormContent';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import SectionCard from '@/src/components/molecule/SectionCard';
import { Form, FormField } from '@/src/components/ui/form';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { TOAST_CONST } from '@/src/constants/toast';
import { useToast } from '@/src/hooks/useToast';
import PATH from '@/src/routes/path';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { PutNoticeByIdRequestBody } from '@/src/apis/rest/notice.dtos';
import { NAV_CONST } from '@/src/constants/nav';
import { useEffect, useRef, useState } from 'react';
import Editor from '@/src/components/atom/Editor';
import PhonePreview from '@/src/components/molecule/PhonePreview';
import dayjs from 'dayjs';
import SectionLabel from '@/src/components/molecule/SectionLabel';
import { usePrompt } from '@/src/hooks/usePrompt';
import { OPTIONS_CONST } from '@/src/constants/options';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import { COMMON_ALERT_DIALOG_CONST } from '@/src/constants/alertDialog';

const BACK_PATH = PATH.NOTICE_DETAIL;

const formErrorMessages = {
  title: '제목을 입력해주세요.',
  contents: '내용을 입력해주세요.',
};

const NoticeFormSchema = () => {
  return z.object({
    title: z.string().min(1, formErrorMessages.title),
    contents: z.string().min(1, formErrorMessages.contents),
    categoryType: z.string(),
    showedYn: z.boolean(),
    fixedTopYn: z.boolean(),
  });
};

const DEFAULT_VALUES: Partial<PutNoticeByIdRequestBody> = {
  title: '',
  contents: '',
  showedYn: true,
  fixedTopYn: true,
};

export default function NoticeEditPage() {
  // util
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();

  // query
  const { data: NoticeData } = useQuery({
    queryKey: ['Notice', id],
    queryFn: () => getNoticeByIdAPI({ id: Number(id) }),
    meta: {
      backUrl: BACK_PATH.replace(':id', String(id)),
      callback: () => {
        navigate(BACK_PATH.replace(':id', String(id)), { replace: true });
      },
    },
  });

  // form
  const form = useForm<PutNoticeByIdRequestBody>({
    resolver: zodResolver(NoticeFormSchema()),
    defaultValues: DEFAULT_VALUES,
    reValidateMode: 'onSubmit',
  });

  // state
  const [defaultData, setDefaultData] = useState('');
  const {
    Dialog: EditDialog,
    isOpen: isEditOpen,
    openDialog: openEditDialog,
    closeDialog: closeEditDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });
  const formRef = useRef<HTMLFormElement>(null);

  // mutation
  const putNoticeMutation = useMutation({ mutationFn: putNoticeByIdAPI });

  // event
  const handleGoBack = () => {
    navigate(-1);
  };
  const handleSubmit = (values: PutNoticeByIdRequestBody) => {
    putNoticeMutation.mutate(
      { ...values, id: Number(id) },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: TOAST_CONST.saveSuccess.title,
            description: TOAST_CONST.saveSuccess.description,
          });

          navigate(BACK_PATH.replace(':id', String(id)));
        },
        onError: e => {
          toast({
            variant: 'error',
            title: TOAST_CONST.error.title,
            description: e.message,
          });
        },
      },
    );
  };

  // render
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={handleGoBack}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>
          <Button
            disabled={
              !form.formState.isValid ||
              !(defaultData !== JSON.stringify(form.watch()))
            }
            type="button"
            onClick={openEditDialog}
            className="w-40"
            buttonSize="medium"
            isLoading={putNoticeMutation.isPending}
          >
            <div className="flex items-center gap-x-2">{BUTTON_CONST.save}</div>
          </Button>
        </div>
      </BottomFixedContainer>
    );
  };

  // effect
  useEffect(() => {
    if (NoticeData) {
      form.reset(NoticeData);
      setDefaultData(JSON.stringify(NoticeData));
    }
  }, [NoticeData, form]);

  // usePrompt
  usePrompt(
    defaultData !== JSON.stringify(form.watch()) && !form.formState.isSubmitted,
  );

  return (
    <>
      <MainContentTitle
        breadcrumbList={[NAV_CONST.notice, NAV_CONST.noticeEdit]}
        pageTitle={NAV_CONST.noticeEdit}
      />
      <Form {...form}>
        <form
          ref={formRef}
          className="relative mb-[100px]"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <SectionCard cardTitle="공지사항 내용">
            <div className="grid grid-cols-[1fr_auto_1fr] gap-x-6 gap-y-6">
              <div className="flex flex-col gap-6">
                <FormField
                  control={form.control}
                  name="categoryType"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent label="유형" error={error}>
                        <RadioGroup
                          optionList={[
                            {
                              label: OPTIONS_CONST.commonCategory,
                              value: 'common',
                            },
                            {
                              label: OPTIONS_CONST.communityCategory,
                              value: 'community',
                            },
                          ]}
                          rootProps={{
                            ...field,
                            onValueChange: field.onChange,
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="showedYn"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent label="노출 여부" error={error}>
                        <RadioGroup
                          optionList={[
                            { label: OPTIONS_CONST.showed, value: 'true' },
                            { label: OPTIONS_CONST.notShowed, value: 'false' },
                          ]}
                          rootProps={{
                            value: field.value ? 'true' : 'false',
                            onValueChange: e => field.onChange(e === 'true'),
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="fixedTopYn"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent label="우선 노출 여부" error={error}>
                        <RadioGroup
                          optionList={[
                            { label: OPTIONS_CONST.applied, value: 'true' },
                            { label: OPTIONS_CONST.notApplied, value: 'false' },
                          ]}
                          rootProps={{
                            value: field.value ? 'true' : 'false',
                            onValueChange: e => field.onChange(e === 'true'),
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <SectionLabel
                  labelProps={{
                    className: 'translate-y-2',
                  }}
                  label="조회 수"
                  value={
                    <Input
                      containerClassName="flex-1"
                      value={
                        NoticeData?.viewCount
                          ? `${NoticeData?.viewCount}회`
                          : '0회'
                      }
                      allowClear={false}
                      disabled
                    />
                  }
                />
              </div>
              <div className="border-r border-r-gray1" />
              <div className="flex flex-col gap-6">
                <SectionLabel
                  labelProps={{
                    className: 'translate-y-2',
                  }}
                  label="등록 일시"
                  value={
                    <Input
                      containerClassName="flex-1"
                      value={dayjs(NoticeData?.createdAt).format(
                        'YYYY-MM-DD HH:mm:ss',
                      )}
                      allowClear={false}
                      disabled
                    />
                  }
                />
                <SectionLabel
                  labelProps={{
                    className: 'translate-y-2',
                  }}
                  label="등록 관리자"
                  value={
                    <Input
                      containerClassName="flex-1"
                      value={NoticeData?.administrator?.name || '-'}
                      allowClear={false}
                      disabled
                    />
                  }
                />
              </div>
              <div className="col-span-3 flex gap-6">
                <div className="flex flex-1 flex-col gap-6">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent
                          className="col-span-3"
                          label="제목"
                          isRequired
                          error={error}
                        >
                          <Input
                            containerClassName="flex-1"
                            placeholder="제목을 입력해주세요"
                            onClear={() => field.onChange('')}
                            {...field}
                          />
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="contents"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent
                          className="col-span-3"
                          label="내용"
                          isRequired
                          error={error}
                        >
                          <div className="flex gap-3">
                            <div className="flex-1">
                              <Editor
                                placeholder="내용을 입력해주세요"
                                height={770}
                                value={field.value || ''}
                                onChange={field.onChange}
                              />
                            </div>
                          </div>
                        </FormContent>
                      );
                    }}
                  />
                </div>
                <div className="w-[400px] flex-shrink-0">
                  <PhonePreview>
                    <div className="sticky top-0">
                      <img
                        src="/assets/images/img-notice-header-preview.png"
                        alt="img-notice-header-preview"
                        width="100%"
                        height="auto"
                      />
                      <PhonePreview.Header className="sticky top-0 flex flex-col gap-1 bg-white">
                        <h2 className="break-keep text-h20sb">
                          {form.watch().title || '제목을 입력해주세요'}
                        </h2>
                        <p className="text-b14r text-gray400">
                          {dayjs().format('YYYY.MM.DD HH:mm')}
                        </p>
                      </PhonePreview.Header>
                    </div>
                    <PhonePreview.Content>
                      <div
                        className="ck-content"
                        dangerouslySetInnerHTML={{
                          __html:
                            form.watch().contents || '내용을 입력해주세요',
                        }}
                      />
                    </PhonePreview.Content>
                  </PhonePreview>
                </div>
              </div>
            </div>
          </SectionCard>
          {renderBottomFixedContent()}
        </form>
      </Form>

      {/* 모달 */}
      {isEditOpen && (
        <EditDialog
          open={isEditOpen}
          onOk={() => formRef?.current?.requestSubmit()}
          onOpenChange={e => {
            if (e) {
              openEditDialog();
            } else {
              closeEditDialog();
            }
          }}
          title={COMMON_ALERT_DIALOG_CONST.edit}
          isLoading={putNoticeMutation.isPending}
        />
      )}
    </>
  );
}
